import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Delete, Edit, Save, Close } from "@mui/icons-material";
import { Produtos, IPetPopulated, IUser, IPedido, IItem } from "../types";
import { createAttachment, getPets, searchExternalProducts } from "../services";

interface ModalNewVendaProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  tecnico: IUser | null;
  initialPet?: IPetPopulated | null;
}

const ModalNewVenda: React.FC<ModalNewVendaProps> = ({
  open,
  onClose,
  onSave,
  tecnico,
  initialPet,
}) => {
  const [pets, setPets] = useState<IPetPopulated[]>([]);
  const [selectedProdutos, setSelectedProdutos] = useState<Produtos[]>([]);
  const [selectedPet, setSelectedPet] = useState<IPetPopulated | null>(null);
  const [historyNotes, setHistoryNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para edição de preços de produtos
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [tempProductPrice, setTempProductPrice] = useState<string>("");

  // Estados para pagamento
  const [pagamentoQuitado, setPagamentoQuitado] = useState(false);
  const [valorPago, setValorPago] = useState<number>(0);
  const [formaPagamento, setFormaPagamento] = useState<string>("Dinheiro");
  const [contaBancaria, setContaBancaria] = useState<string>("Caixa Principal");
  const [dataVencimento, setDataVencimento] = useState<string>("");

  // Estados para pesquisa externa de produtos
  const [externalProducts, setExternalProducts] = useState<any[]>([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");

  useEffect(() => {
    if (open) {
      // Reset states when modal opens
      setSelectedProdutos([]);
      setSelectedPet(initialPet || null); // Define o pet inicial se fornecido
      setHistoryNotes("");
      setError(null);
      setPagamentoQuitado(false);
      setValorPago(0);
      setFormaPagamento("Dinheiro");
      setContaBancaria("Caixa Principal");
      setDataVencimento("");
      // Reset estados de pesquisa externa
      setExternalProducts([]);
      setProductSearchTerm("");
      setIsSearchingProducts(false);
      loadData();
    }
  }, [open, initialPet]);

  // Funções de formatação de moeda
  const formatarMoeda = (valor: number): string => {
    return (valor / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorDigitado = e.target.value.replace(/[^\d]/g, "");
    const valorNumerico = parseInt(valorDigitado || "0", 10);
    setValorPago(valorNumerico);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir: backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Permitir: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }
    // Permitir apenas números
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  // Atualizar valor pago automaticamente quando quitado
  useEffect(() => {
    if (pagamentoQuitado) {
      const valorTotal = selectedProdutos.reduce(
        (sum, produto) => sum + getEffectivePrice(produto),
        0
      );
      // Converter para centavos para compatibilidade com o sistema de moeda inteligente
      setValorPago(Math.round(valorTotal * 100));
    }
  }, [pagamentoQuitado, selectedProdutos]);

  // Função para pesquisar produtos externos
  const searchProducts = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setExternalProducts([]);
      return;
    }

    setIsSearchingProducts(true);
    try {
      const results = await searchExternalProducts(searchTerm);
      setExternalProducts(results);
    } catch (error) {
      setExternalProducts([]);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  // Debounce para pesquisa de produtos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchTerm) {
        searchProducts(productSearchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const loadData = async () => {
    try {
      const [petsData] = await Promise.allSettled([getPets()]);

      if (petsData.status === "fulfilled" && petsData.value) {
        setPets(petsData.value as IPetPopulated[]);
      }
    } catch (error) {
      setError("Erro ao carregar dados necessários");
    }
  };

  const removeProduto = (produtoId: string) => {
    setSelectedProdutos((prev) => prev.filter((p) => p.ID !== produtoId));
  };

  // Função auxiliar para obter o preço efetivo (editado ou original)
  const getEffectivePrice = (item: Produtos): number => {
    return item.editedPrice !== undefined ? item.editedPrice : item.PrecoVenda;
  };

  // Handlers para edição de preço de produtos
  const handleEditProductPrice = (productId: string, currentPrice: number) => {
    setEditingProductId(productId);
    setTempProductPrice(currentPrice.toString());
  };

  const handleSaveProductPrice = (productId: string) => {
    const newPrice = parseFloat(tempProductPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      setError("Preço inválido");
      return;
    }

    setSelectedProdutos((prev) =>
      prev.map((p) => {
        if (p.ID === productId) {
          return {
            ...p,
            originalPrice: p.originalPrice ?? p.PrecoVenda,
            editedPrice: newPrice,
          };
        }
        return p;
      })
    );

    setEditingProductId(null);
    setTempProductPrice("");
  };

  const handleCancelProductPriceEdit = () => {
    setEditingProductId(null);
    setTempProductPrice("");
  };

  const handleSubmit = async () => {
    if (selectedProdutos.length === 0 || !selectedPet) {
      setError("Pelo menos um produto e um Pet são obrigatórios");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const items: IItem[] = [];

      // Adiciona produtos selecionados
      selectedProdutos.forEach((produto) => {
        const effectivePrice = getEffectivePrice(produto);
        items.push({
          Codigo: produto.Codigo,
          Unidade: produto.UnidadeComercial,
          Descricao: produto.Nome,
          Quantidade: 1,
          ValorUnitario: effectivePrice,
          ValorFrete: 0,
          DescontoUnitario: 0,
          ValorTotal: effectivePrice,
          PesoKG: produto.PesoKG,
          Comprimento: produto.Comprimento,
          Altura: produto.Altura,
          Largura: produto.Largura,
          FreteGratis: false,
          ValorUnitarioFrete: 0,
          PrazoEntregaFrete: 0,
          ComissaoVendedor: 0,
          Seguro: 0,
          AgrupamentoItensVenda: "Grupo de Produtos Padrão - 01",
        });
      });

      const dataToSend: IPedido = {
        OrigemVenda: "Venda Direta",
        DepositoID: "683df6ce93591480c838eb1b",
        Deposito: "PADRÃO",
        StatusSistema: "Pedido",
        Categoria: "Venda de Produtos",
        Descricao: `${selectedPet.name}${historyNotes.trim() ? ` - ${historyNotes}` : ""}`,
        Validade: new Date().toISOString(),
        Empresa: "PETSHOP DA MADRE",
        EmpresaID: "683df6ce93591480c838eb1a",
        ClienteID: selectedPet.tutor.idERP || selectedPet.tutor.erpId || "",
        Cliente: selectedPet.tutor.name,
        PessoaID: selectedPet.tutor.idERP || selectedPet.tutor.erpId || "",
        ClienteCNPJ: selectedPet.tutor.cpf,
        ClienteEmail: selectedPet.tutor.email ?? "",
        Vendedor: tecnico?.NomeFantasia ?? "",
        DataEnvio: new Date().toISOString(),
        Municipio: "Londrina",
        CodigoMunicipio: "4113700",
        Pais: "Brasil",
        Cep: selectedPet.tutor.cep || selectedPet.tutor.addressZip || "",
        Uf: "PR",
        UfCodigo: "41",
        Bairro:
          selectedPet.tutor.bairro || selectedPet.tutor.addressDistrict || "",
        Logradouro:
          selectedPet.tutor.logradouro || selectedPet.tutor.addressStreet || "",
        Items: items,
        Data: new Date().toISOString(),
        LancarComissaoVendedor: false,
        OrdemServico: {
          OrdemServicoConcluida: false,
          OrdemServicoCancelada: false,
          Problema: historyNotes || "Venda de produtos",
          Laudo: historyNotes || "Venda de produtos realizada",
          DataAbertura: new Date().toISOString(),
          Tecnico: tecnico?.NomeFantasia ?? "",
          DataVisita: new Date().toISOString(),
          DataInicioIntervencaoTecnica: new Date().toISOString(),
          Laudos: [
            {
              Tecnico: tecnico?.NomeFantasia ?? "",
              DataRegistro: new Date().toISOString(),
              LaudoTecnicoGeral: historyNotes || "Venda de produtos realizada",
            },
          ],
        },
        Atributos: [
          {
            Nome: "PET",
            Model: selectedPet.name,
          },
        ],
        // Campos para lançamento financeiro
        pagamentoQuitado,
        valorPago: valorPago / 100, // Converter de centavos para reais
        formaPagamento,
        contaBancaria,
        dataVencimento,
      };

      await createAttachment(dataToSend);

      onSave();
      handleClose();
    } catch (error) {
      setError("Erro ao criar venda. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedProdutos([]);
    setSelectedPet(null);
    setHistoryNotes("");
    setError(null);
    onClose();
  };

  const totalVenda = selectedProdutos.reduce(
    (sum, produto) => sum + getEffectivePrice(produto),
    0
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Nova Venda de Produto</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <Autocomplete
              options={pets}
              getOptionLabel={(option) =>
                `${option.name} - ${option.tutor.name}`
              }
              value={selectedPet}
              onChange={(_, newValue) => {
                setSelectedPet(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pet *"
                  placeholder="Pesquise pelo nome do pet ou tutor..."
                  required
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const lowerInput = inputValue.toLowerCase();
                return options.filter(
                  (pet) =>
                    pet.name.toLowerCase().includes(lowerInput) ||
                    pet.tutor.name.toLowerCase().includes(lowerInput)
                );
              }}
              noOptionsText="Nenhum pet encontrado"
            />
          </Grid>

          <Grid size={12}>
            <Autocomplete
              options={productSearchTerm.length >= 2 ? externalProducts : []}
              getOptionLabel={(option) =>
                `${option.Nome} - R$ ${option.PrecoVenda.toFixed(2)}`
              }
              value={null}
              onChange={(_, newValue) => {
                if (newValue) {
                  setSelectedProdutos((prev) => [...prev, newValue]);
                }
              }}
              onInputChange={(_, newInputValue) => {
                setProductSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Adicionar Produto"
                  placeholder="Digite para pesquisar produtos..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingProducts && (
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            Pesquisando...
                          </Typography>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={(options) => options} // Não filtrar aqui, já filtramos na API
              noOptionsText={
                isSearchingProducts
                  ? "Pesquisando..."
                  : productSearchTerm.length < 2
                    ? "Digite pelo menos 2 caracteres para pesquisar"
                    : "Nenhum produto encontrado"
              }
              loading={isSearchingProducts}
            />
            {selectedProdutos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Produtos Selecionados:
                </Typography>
                {selectedProdutos.map((produto) => (
                  <Box
                    key={produto.ID}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      p: 1,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <strong>{produto.Nome}</strong>
                    </Typography>
                    {editingProductId !== produto.ID ? (
                      <>
                        <Typography variant="body2" color="primary.main">
                          R$ {getEffectivePrice(produto).toFixed(2)}
                        </Typography>
                        {produto.editedPrice !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            R$ {produto.PrecoVenda.toFixed(2)}
                          </Typography>
                        )}
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditProductPrice(
                              produto.ID,
                              getEffectivePrice(produto)
                            )
                          }
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeProduto(produto.ID)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          size="small"
                          value={tempProductPrice}
                          onChange={(e) => setTempProductPrice(e.target.value)}
                          placeholder="0,00"
                          sx={{ width: 120 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R$
                              </InputAdornment>
                            ),
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleSaveProductPrice(produto.ID)}
                          color="success"
                        >
                          <Save fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelProductPriceEdit}
                          color="error"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Observações"
              value={historyNotes}
              onChange={(e) => setHistoryNotes(e.target.value)}
              placeholder="Observações sobre a venda..."
            />
          </Grid>

          {/* Seção de Pagamento */}
          <Grid size={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Informações de Pagamento
            </Typography>
          </Grid>

          <Grid size={6}>
            <TextField
              fullWidth
              label="Valor Pago"
              type="text"
              value={formatarMoeda(valorPago)}
              onChange={handleValorChange}
              onKeyDown={handleKeyDown}
              disabled={pagamentoQuitado}
              inputProps={{
                inputMode: "numeric",
              }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
              }}
              helperText={
                pagamentoQuitado
                  ? "Valor calculado automaticamente"
                  : "Digite o valor recebido (ex: 1234 = R$ 12,34)"
              }
            />
          </Grid>

          <Grid size={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pagamentoQuitado}
                  onChange={(e) => setPagamentoQuitado(e.target.checked)}
                />
              }
              label="Pagamento Quitado"
            />
          </Grid>

          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select
                value={formaPagamento}
                label="Forma de Pagamento"
                onChange={(e) => setFormaPagamento(e.target.value)}
              >
                <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
                <MenuItem value="Cartão de Débito">Cartão de Débito</MenuItem>
                <MenuItem value="PIX">PIX</MenuItem>
                {/* <MenuItem value="Transferência">Transferência</MenuItem> */}
                <MenuItem value="Boleto Bancário">Boleto Bancário</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel>Conta Bancária</InputLabel>
              <Select
                value={contaBancaria}
                label="Conta Bancária"
                onChange={(e) => setContaBancaria(e.target.value)}
              >
                <MenuItem value="Caixa Principal">Caixa Principal</MenuItem>
                <MenuItem value="Conta Corrente">Conta Corrente</MenuItem>
                <MenuItem value="Conta Poupança">Conta Poupança</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}

          <Grid size={6}>
            <TextField
              fullWidth
              label="Data de Vencimento"
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Data limite para pagamento"
            />
          </Grid>

          {selectedProdutos.length > 0 && selectedPet && (
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="subtitle2">Resumo da Venda:</Typography>
                <Typography variant="body2">
                  <strong>Pet:</strong> {selectedPet.name} (
                  {selectedPet.tutor.name})
                </Typography>
                {/* <Typography variant="body2">
                  <strong>Produtos:</strong>
                </Typography>
                {selectedProdutos.map((produto) => (
                  <Typography key={produto.ID} variant="body2" sx={{ ml: 2 }}>
                    • {produto.Nome} - R$ {produto.PrecoVenda.toFixed(2)}
                  </Typography>
                ))} */}
                <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                  <strong>Total: R$ {totalVenda.toFixed(2)}</strong>
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isSubmitting || selectedProdutos.length === 0 || !selectedPet
          }
        >
          {isSubmitting ? "Criando..." : "Criar Venda"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNewVenda;
